import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_OR_UPDATE_AUTO_RENEWAL_PREFERENCE,
  CANCEL_AUTO_RENEWAL_PREFERENCE,
} from "./renewal-preference.mutation";
import { GET_AUTO_RENEWAL_USER_PREFERENCES, GET_AUTO_RENEWAL_PREFERENCE } from "./renewal-preference.query";
import {
  IAutoRenewalPreferenceInput,
  IAutoRenewalPreferenceResponse,
  IGetAutoRenewalUserPreferencesResponse,
  IGetAutoRenewalPreferenceResponse,
  ICancelAutoRenewalPreferenceResponse,
  PaymentMethodProvider,
} from "./renewal-preference.interface";

export const useCreateOrUpdateRenewalPreference = () => {
  const [createOrUpdatePreference, { data, loading, error }] = useMutation<{
    createOrUpdateAutoRenewalPreference: IAutoRenewalPreferenceResponse;
  }>(CREATE_OR_UPDATE_AUTO_RENEWAL_PREFERENCE);

  const saveRenewalPreference = <T extends PaymentMethodProvider>(
    preferenceInput: IAutoRenewalPreferenceInput<T>,
  ) => {
    return createOrUpdatePreference({
      variables: {
        preferenceInput,
      },
      context: { suppressGlobalErrorToast: true },
    });
  };

  return {
    saveRenewalPreference,
    response: data?.createOrUpdateAutoRenewalPreference,
    loading,
    error,
  };
};

export const useGetAutoRenewalUserPreferences = () => {
  const { data, loading, error, refetch } = useQuery<{
    getAutoRenewalUserPreferences: IGetAutoRenewalUserPreferencesResponse;
  }>(GET_AUTO_RENEWAL_USER_PREFERENCES);

  return {
    preferences: data?.getAutoRenewalUserPreferences?.preferences || [],
    loading,
    error,
    refetch,
  };
};

export const useGetAutoRenewalPreference = (currentSubscriptionId: string) => {
  const { data, loading, error, refetch } = useQuery<{
    getAutoRenewalPreferences: IGetAutoRenewalPreferenceResponse;
  }>(GET_AUTO_RENEWAL_PREFERENCE, {
    variables: { currentSubscriptionId },
    skip: !currentSubscriptionId,
  });

  return {
    preference: data?.getAutoRenewalPreferences?.preference,
    loading,
    error,
    refetch,
  };
};

export const useCancelAutoRenewalPreference = () => {
  const [cancelPreference, { data, loading, error }] = useMutation<{
    cancelAutoRenewalPreference: ICancelAutoRenewalPreferenceResponse;
  }>(CANCEL_AUTO_RENEWAL_PREFERENCE);

  const cancelRenewalPreference = (currentSubscriptionId: string) => {
    return cancelPreference({
      variables: {
        currentSubscriptionId,
      },
    });
  };

  return {
    cancelRenewalPreference,
    response: data?.cancelAutoRenewalPreference,
    loading,
    error,
  };
};

